import React, { useEffect, useState } from "react";
import { EthernetPort, Network, Calendar, Activity, RefreshCw } from "lucide-react";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";

// Helper for date format
function formatDate(dateString?: string) {
    if (!dateString) return "-";
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    return d.toLocaleString();
}

// Helper for byte formatting
const formatBytes = (bytes: number | string | null | undefined) => {
    if (bytes === null || bytes === undefined || bytes === "") return "-";
    const num = Number(bytes);
    if (isNaN(num) || num === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB", "PB"];
    const i = Math.floor(Math.log(num) / Math.log(k));
    return parseFloat((num / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// Helper for number formatting
const formatNumber = (num: number | string | null | undefined) => {
    if (num === null || num === undefined || num === "") return "-";
    const n = Number(num);
    if (isNaN(n)) return String(num);
    return n.toLocaleString();
};

const LoadingSpinner = ({ section }: { section: string }) =>
    <div className="text-gray-200 text-xs py-4 italic">Loading {section}...</div>;

interface NetworkInterfacesProps {
    agent_id: string;
    organization_id: string;
    asset_id: string;
    client_id: string;
    platform?: string;
}

const NetworkInterfaces: React.FC<NetworkInterfacesProps> = ({
    agent_id,
    organization_id,
    asset_id,
    client_id,
    platform,
}) => {
    const [interfaces, setInterfaces] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    const info = JSON.parse(sessionStorage.getItem("internal_ids") || "{}");
    const platformStr = (platform || info?.platform || info?.os_name || "").toLowerCase();
    const isWindows = (platformStr === "windows" || platformStr.includes("windows") || (platformStr.startsWith("win") && !platformStr.includes("darwin")));

    async function fetchInterfaces() {
        setLoading(true);
        try {
            const params = {
                query: JSON.stringify({ client_id: client_id || info?.client_id, organization_id: organization_id || info?.organization_id, agent_id: agent_id || info?.agent_id, asset_id: asset_id || info?.asset_id }),
            };
            const response = await baseapi.get("/asset_interfaces", { params });
            setInterfaces(response.data?.data ?? []);
        } catch (err: any) {
            showError(err.message);
            setInterfaces([]);
        }
        setLoading(false);
    }
    useEffect(() => {
        fetchInterfaces();
    }, [client_id, organization_id, agent_id, asset_id]);

    return (
        <div className="p-3">
            {/* Compact Header */}
            <div className="flex items-center justify-between mb-3 border-b border-gray-700 pb-2">
                <div className="flex items-center">
                    <div className="p-1.5 bg-orange-600 rounded mr-2.5">
                        <EthernetPort className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-white leading-tight">Network Interfaces</h2>
                        <p className="text-gray-200 text-[10px] uppercase font-semibold tracking-wider">
                            {interfaces?.length || 0} interfaces configured
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => fetchInterfaces()}
                        disabled={loading}
                        className="p-2 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white border border-gray-700 rounded-md transition-colors shadow-sm flex items-center justify-center disabled:opacity-50 h-8 cursor-pointer"
                        title="Refresh data"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-orange-500' : ''}`} />
                    </button>
                </div>
            </div>

            <div className="space-y-3">
                {loading ? (
                    <LoadingSpinner section="network interfaces" />
                ) : interfaces && interfaces.length > 0 ? (
                    interfaces.map((iface) => {
                        const showTraffic = !isWindows;

                        return (
                            <div key={iface.id} className="bg-gray-900/40 rounded-md border border-gray-700 overflow-hidden hover:border-teal-500/30 transition-colors">
                                {/* Header - Compact */}
                                <div className="px-3 py-2 bg-gray-800/40 border-b border-gray-700/50 flex items-center justify-between">
                                    <div className="flex items-center space-x-2 truncate">
                                        <EthernetPort className="w-3.5 h-3.5 text-orange-500 shrink-0" />
                                        <div className="truncate">
                                            <h3 className="text-xs font-bold text-white truncate">{iface.friendly_name}</h3>
                                            <p className="text-[10px] text-gray-200 truncate italic">{iface.description}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1.5 shrink-0">
                                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border uppercase ${iface.status === 'Up'
                                            ? 'bg-green-500/10 text-green-400 border-green-500/20'
                                            : 'bg-red-500/10 text-red-400 border-red-500/20'
                                            }`}>
                                            {iface.status}
                                        </span>
                                        {iface.physical_adapter && (
                                            <span className="px-1.5 py-0.5 bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded text-[9px] font-bold uppercase">
                                                PHYSICAL
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Content Grid - High Density */}
                                <div className={`p-2.5 grid grid-cols-1 ${isWindows ? 'md:grid-cols-3' : 'md:grid-cols-2 lg:grid-cols-4'} gap-3`}>
                                    {/* Column 1: Core Stats */}
                                    <div className="space-y-1.5">
                                        <div className="flex justify-between items-center p-1.5 bg-gray-800/20 rounded border border-gray-700/30">
                                            <span className="text-[10px] text-gray-200 font-bold uppercase">Type</span>
                                            <span className="text-[11px] text-gray-300 font-medium">{iface.interface_type || "-"}</span>
                                        </div>
                                        <div className="flex justify-between items-center p-1.5 bg-gray-800/20 rounded border border-gray-700/30">
                                            <span className="text-[10px] text-gray-200 font-bold uppercase">MAC</span>
                                            <span className="text-[11px] text-white font-mono">{iface.mac || "-"}</span>
                                        </div>
                                        <div className="flex justify-between items-center p-1.5 bg-gray-800/20 rounded border border-gray-700/30">
                                            <span className="text-[10px] text-gray-200 font-bold uppercase">Speed</span>
                                            <span className="text-[11px] text-teal-400 font-bold">{iface.link_speed || "-"}</span>
                                        </div>
                                    </div>

                                    <div className="space-y-1.5">
                                        <div className="p-1.5 bg-gray-800/20 rounded border border-gray-700/30 min-h-[70px]">
                                            <p className="text-[9px] font-bold text-cyan-400 uppercase mb-1 flex items-center">
                                                <Network className="w-2.5 h-2.5 mr-1" /> IP & Subnet
                                            </p>
                                            <div className="space-y-1 pt-0.5">
                                                <div className="flex justify-between items-center text-[10px]">
                                                    <span className="text-gray-400 font-bold uppercase text-[9px]">IP Address</span>
                                                    <span className="text-white font-mono">{iface.address || "-"}</span>
                                                </div>
                                                <div className="flex justify-between items-center text-[10px]">
                                                    <span className="text-gray-400 font-bold uppercase text-[9px]">Subnet Mask</span>
                                                    <span className="text-gray-200 font-mono">{iface.mask || "-"}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Column 3 for Linux and macOS: Traffic & Packets */}
                                    {showTraffic && (
                                        <div className="space-y-1.5">
                                            <div className="p-1.5 bg-gray-800/20 rounded border border-gray-700/30 min-h-[70px]">
                                                <p className="text-[9px] font-bold text-emerald-400 uppercase mb-1 flex items-center">
                                                    <Activity className="w-2.5 h-2.5 mr-1" /> Traffic & Packets
                                                </p>
                                                <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-[10px]">
                                                    <div>
                                                        <span className="text-gray-400 text-[9px] block">Bytes In</span>
                                                        <span className="text-white font-mono font-medium text-[10px]" title={iface.ibytes !== undefined && iface.ibytes !== null ? `${iface.ibytes} bytes` : ""}>
                                                            {formatBytes(iface.ibytes)}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-400 text-[9px] block">Bytes Out</span>
                                                        <span className="text-white font-mono font-medium text-[10px]" title={iface.obytes !== undefined && iface.obytes !== null ? `${iface.obytes} bytes` : ""}>
                                                            {formatBytes(iface.obytes)}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-400 text-[9px] block">Packets In</span>
                                                        <span className="text-gray-200 font-mono font-medium text-[10px]">
                                                            {formatNumber(iface.ipackets)}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-400 text-[9px] block">Packets Out</span>
                                                        <span className="text-gray-200 font-mono font-medium text-[10px]">
                                                            {formatNumber(iface.opackets)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Column 4 (or 3 on Windows): System Details */}
                                    <div className="space-y-1">
                                        <div className="flex justify-between text-[10px] px-1">
                                            <span className="text-gray-200 font-bold">INDEX</span>
                                            <span className="text-gray-300 font-mono">{iface.interface ?? "-"}</span>
                                        </div>
                                        <div className="flex justify-between text-[10px] px-1">
                                            <span className="text-gray-200 font-bold flex items-center"><Calendar className="w-2.5 h-2.5 mr-1" /> CREATED</span>
                                            <span className="text-gray-200">{formatDate(iface.created).split(',')[0]}</span>
                                        </div>
                                        <div className="flex justify-between text-[10px] px-1">
                                            <span className="text-gray-200 font-bold flex items-center"><Activity className="w-2.5 h-2.5 mr-1" /> UPDATED</span>
                                            <span className="text-gray-200">{formatDate(iface.updated).split(',')[0]}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="text-center py-8 bg-gray-900/20 rounded-md border border-gray-700 border-dashed">
                        <EthernetPort className="w-6 h-6 text-gray-600 mx-auto mb-2" />
                        <h3 className="text-sm font-bold text-gray-200">No Network Interfaces</h3>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NetworkInterfaces;
